<template>
 <v-app class="bg-white">
    <!-- Header with AutoSync Logo -->
    <v-app-bar
      elevation="0"
      color="transparent"
      absolute
      class="sweepstakes-header"
    >
      <v-container>
        <v-row align="center" justify="space-between">
          <!-- Logo -->
          <v-col cols="auto" class="text-center">
            <router-link to="/" class="text-decoration-none">
              <img
                src="@/assets/img/logo.png"
                alt="AutoSync Logo"
                class="autosync-logo mt-5"
                :height="$vuetify.breakpoint.xs ? 120 : 150"
              />
            </router-link>
          </v-col>

          <!-- Login/User Menu -->
          <v-col cols="auto" class="text-center">
            <div class="header-actions mt-5">
              <!-- Show login button if not logged in -->
              <v-btn
                v-if="!isLoggedIn"
                color="orange"
                outlined
                class="login-btn mr-2"
                @click="goToLogin"
              >
                Login
              </v-btn>

              <!-- Show user menu if logged in -->
              <v-menu v-else bottom offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    color="orange"
                    v-bind="attrs"
                    v-on="on"
                    class="user-menu-btn"
                  >
                    <v-icon left small>mdi-account-circle</v-icon>
                    {{ userDisplayName }}
                    <v-icon right small>mdi-chevron-down</v-icon>
                  </v-btn>
                </template>

                <v-list>
                  <v-list-item @click="goToReferrals">
                    <v-list-item-content>
                      <v-list-item-title>My Referrals</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item @click="goToProfile">
                    <v-list-item-content>
                      <v-list-item-title>My Profile</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>

                  <v-divider></v-divider>

                  <v-list-item @click="logout">
                    <v-list-item-content>
                      <v-list-item-title>Logout</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-app-bar>

    <v-main style="display: contents;">
      <div class="sweepstakes-landing">
        <!-- Hero Section -->
        <section class="hero-section">
          <div class="hero-background">
            <div class="hero-video-container">
              <img
                :src="activeCampaign.hero_image_url"
                alt="Campaign Hero Image"
                class="hero-image"
                @error="onHeroImageError"
              />
              <div class="hero-overlay"></div>
            </div>

            <v-container class="hero-content">
              <v-row justify="center" align="center" class="fill-height">
                <v-col cols="12" lg="10" xl="8">
                  <v-row align="center">
                    <!-- Left Side - Content -->
                    <v-col cols="12" md="6" class="text-center text-md-left">
                      <div class="hero-badge mb-4">
                        <v-chip color="orange" text-color="white" large>
                          <v-icon left>mdi-fire</v-icon>
                          LIMITED TIME OFFER
                        </v-chip>
                      </div>

                      <h1 class="hero-title white--text mb-4">
                        {{ activeCampaign ? activeCampaign.title : 'WIN A BRAND NEW 2024 LUXURY CAR + $10,000 CASH!' }}
                      </h1>

                      <p class="hero-subtitle white--text mb-6">
                        {{ activeCampaign ? activeCampaign.description : 'Enter for your chance to win an amazing prize package worth over $50,000. Don\'t miss this incredible opportunity!' }}
                      </p>

                      <!-- Prize Value -->
                      <div class="prize-value-card mb-6">
                        <div class="prize-amount">${{ activeCampaign ? formatPrice(activeCampaign.prize_value) : '50,000' }}</div>
                        <div class="prize-label">TOTAL VALUE</div>
                      </div>

                      <!-- Countdown Timer -->
                      <div class="countdown-wrapper mb-6">
                        <countdown-timer
                          v-if="activeCampaign && activeCampaign.end_date"
                          :end-time="activeCampaign.end_date"
                        />
                      </div>

                      <!-- CTA Button -->
                      <v-btn
                        x-large
                        color="orange"
                        class="cta-button elevation-8"
                        @click="showEntryForm = true"
                        :disabled="!activeCampaign || !isActive"
                      >
                        ENTER TO WIN NOW
                      </v-btn>

                      <div class="trust-indicators mt-4">
                        <small class="white--text">
                          <v-icon small color="green" class="mr-1 material-icons-round">verified_user</v-icon>
                          100% Secure & Trusted
                        </small>
                      </div>
                    </v-col>

                    <!-- Right Side - Car Image -->
                    <v-col cols="12" md="6" class="text-center">
                      <div class="car-showcase">
                        <img
                          :src="activeCampaign.prize_image_url "
                          alt="Prize Image"
                          class="prize-car-image"
                          @error="onPrizeImageError"
                        />
                        <div class="car-glow"></div>
                      </div>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-container>
          </div>
        </section>

        <!-- Features Section -->
        <section class="features-section py-16">
          <v-container>
            <v-row justify="center">
              <v-col cols="12" class="text-center mb-12">
                <h2 class="section-title">Why Enter Our Sweepstakes?</h2>
                <p class="section-subtitle">Join thousands of winners who have already claimed their prizes</p>
              </v-col>

              <v-col cols="12" md="4" v-for="(feature, index) in features" :key="index">
                <div class="feature-card text-center">
                  <div class="feature-icon mb-4">
                    <v-avatar size="80" :color="feature.color" class="elevation-4">
                      <v-icon class="material-icons-round" size="40" color="white">{{ feature.icon }}</v-icon>
                    </v-avatar>
                  </div>
                  <h4 class="feature-title mb-3">{{ feature.title }}</h4>
                  <p class="feature-description">{{ feature.description }}</p>
                </div>
              </v-col>
            </v-row>
          </v-container>
        </section>

        <!-- Stats Section -->
        <section class="stats-section py-16">
          <v-container>
            <v-row>
              <v-col cols="6" md="3" v-for="(stat, index) in stats" :key="index" class="text-center">
                <div class="stat-card">
                  <div class="stat-number">{{ stat.number }}</div>
                  <div class="stat-label">{{ stat.label }}</div>
                </div>
              </v-col>
            </v-row>
          </v-container>
        </section>

        <!-- User Entry Form Modal -->
        <v-dialog v-model="showEntryForm" max-width="600" persistent>
          <v-card class="entry-form-card">
            <v-card-title class="entry-form-header">
              <div class="form-header-content">
                <div>
                  <h3 class="white--text mb-0">Enter Your Information</h3>
                  <p class="white--text mb-0 opacity-80">Just a few details to get started</p>
                </div>
              </div>
              <v-spacer></v-spacer>
              <v-btn icon @click="showEntryForm = false" color="white">
                <v-icon class="material-icons-round">close</v-icon>
              </v-btn>
            </v-card-title>

            <v-card-text class="pa-8">
              <v-form ref="entryForm" v-model="formValid">
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="userForm.first_name"
                      label="First Name"
                      outlined
                      dense
                      class="font-size-input input-style py-0"
                      style="margin-bottom: -25px;"
                      :rules="[rules.required]"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="userForm.last_name"
                      label="Last Name"
                      outlined
                      dense
                      style="margin-bottom: -25px;"
                      class="font-size-input input-style py-0"
                      :rules="[rules.required]"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-text-field
                      v-model="userForm.email"
                      label="Email Address"
                      outlined
                      type="email"
                      style="margin-bottom: -25px;"
                      dense
                      color="#e91e63"
                      class="font-size-input input-style py-0"
                      :rules="[rules.required, rules.email]"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-text-field
                      v-model="userForm.phone"
                      v-mask="'(###) ###-####'"
                      label="Phone Number"
                      placeholder="(555) 123-4567"
                      outlined
                      dense
                      class="font-size-input input-style py-0"
                      :rules="[rules.required, rules.phone]"
                    ></v-text-field>
                  </v-col>
                </v-row>

                <div class="text-center mt-6">
                  <v-btn
                    x-large
                    color="orange"
                    class="continue-btn"
                    @click="proceedToTiers"
                    :disabled="!formValid"
                    :loading="submitting"
                  >
                    Continue to Entry Options
                  </v-btn>
                </div>

                <!-- Login Option -->
                <div class="text-center mt-4">
                  <v-divider class="mb-4"></v-divider>
                  <p class="grey--text mb-2">Already have an account?</p>
                  <v-btn
                    outlined
                    color="blue"
                    @click="goToLoginFromModal"
                    class="login-from-modal-btn"
                  >
                    Login to Your Account
                  </v-btn>
                </div>

                <div class="text-center mt-4">
                  <small class="grey--text">
                    <v-icon small color="green" class="mr-1 material-icons-round">verified_user</v-icon>
                    Your information is secure and will never be shared
                  </small>
                </div>
              </v-form>
            </v-card-text>
          </v-card>
        </v-dialog>

        <!-- Footer -->
        <footer class="sweepstakes-footer py-8">
          <v-container>
            <v-row justify="center">
              <v-col cols="12" class="text-center">
                <div class="footer-links mb-4">
                  <router-link
                    to="/sweepstakes/terms"
                    class="footer-link"
                  >
                    Terms & Conditions
                  </router-link>
                  <span class="mx-3">|</span>
                  <a href="mailto:support@autosyncx.com" class="footer-link">
                    Contact Support
                  </a>
                </div>
                <p class="footer-text mb-0">
                  © 2025 AutoSyncX™ / Affiliate Car LLC. All rights reserved.
                </p>
                <p class="footer-disclaimer mt-2">
                  <small>
                    NO PURCHASE NECESSARY. Must be 18+ and US resident.
                    <router-link to="/sweepstakes/terms" class="footer-link">
                      See official rules
                    </router-link>
                    for complete details.
                  </small>
                </p>
              </v-col>
            </v-row>
          </v-container>
        </footer>

        <!-- Loading State -->
        <v-overlay :value="loading">
          <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>
      </div>
    </v-main>
  </v-app>
</template>

<script>
import CountdownTimer from './CountdownTimer.vue'

export default {
  name: 'SweepstakesLanding',
  directives: {
  },
  components: {
    CountdownTimer
  },
  data() {
    return {
      loading: true,
      activeCampaign: null,
      timeRemaining: null,
      referralCode: null, // Store referral code from URL
      showEntryForm: false,
      currentUser: null, // Store current user info
      formValid: false,
      submitting: false,
      userForm: {
        first_name: '',
        last_name: '',
        email: '',
        phone: ''
      },
      rules: {
        required: value => !!value || 'This field is required',
        email: value => {
          const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
          return pattern.test(value) || 'Please enter a valid email address'
        },
        phone: value => {
          // Remove mask characters and validate
          const cleanPhone = value ? value.replace(/[\s\-\(\)]/g, '') : ''
          const pattern = /^\d{10}$/
          return pattern.test(cleanPhone) || 'Please enter a valid 10-digit phone number'
        }
      },
      features: [
        {
          icon: 'emoji_events',
          color: 'orange',
          title: 'Amazing Prizes',
          description: 'Win incredible prizes worth thousands of dollars including luxury cars and cash!'
        },
        {
          icon: 'verified_user',
          color: 'green',
          title: '100% Secure',
          description: 'Your information is completely secure and protected with industry-standard encryption.'
        },
        {
          icon: 'bolt',
          color: 'blue',
          title: 'Quick & Easy',
          description: 'Enter in just minutes! Simple process with instant confirmation of your entry.'
        }
      ],
      stats: [
        { number: '50K+', label: 'Total Entries' },
        { number: '$2M+', label: 'Prizes Given' },
        { number: '1,200+', label: 'Winners' },
        { number: '99.9%', label: 'Satisfaction' }
      ]
    }
  },
  computed: {
    isActive() {
      if (!this.activeCampaign) return false
      const now = new Date()
      const endDate = new Date(this.activeCampaign.end_date)
      return now < endDate
    },

    isLoggedIn() {
      return !!localStorage.getItem('token')
    },

    userDisplayName() {
      if (this.currentUser) {
        return this.currentUser.first_name || 'User'
      }
      return 'User'
    }
  },
  async mounted() {
    // Capture referral code from URL
    this.referralCode = this.$route.query.ref || null
    if (this.referralCode) {
      console.log('Referral code detected:', this.referralCode)
      // Store in localStorage for checkout process
      localStorage.setItem('sweepstakes_referral_code', this.referralCode)
    }

    await this.loadCampaigns()
    await this.loadCurrentUser()
  },
  methods: {
    async loadCampaigns() {
      try {
        this.loading = true
        const response = await window.axios.get('api/sweepstakes/campaigns')

        if (response.data.flag === 1 && response.data.campaigns.length > 0) {
          this.activeCampaign = response.data.campaigns[0] // Get the first active campaign
          this.timeRemaining = this.activeCampaign.time_remaining
        } else {
          // Fallback: Use mock data if no campaigns found
          console.warn('No campaigns found, using mock data')
          this.activeCampaign = this.getMockCampaign()
        }
      } catch (error) {
        console.error('Error loading campaigns:', error)
        // Use mock data as fallback
        this.activeCampaign = this.getMockCampaign()
        console.warn('Using mock campaign data due to API error')
      } finally {
        this.loading = false
      }
    },

    getMockCampaign() {
      const endDate = new Date()
      endDate.setDate(endDate.getDate() + 30) // 30 days from now

      return {
        id: 1,
        title: 'Win a Brand New Car + $10,000 Cash!',
        description: 'Enter for your chance to win an amazing prize package including a brand new vehicle and $10,000 cash. Don\'t miss this incredible opportunity!',
        prize_description: 'Brand New 2024 Vehicle + $10,000 Cash Prize',
        prize_value: 50000,
        start_date: new Date().toISOString(),
        end_date: endDate.toISOString(),
        active: true,
        bonus_promotion: {
          multiplier: 2,
          description: 'Double your entries with our special bonus offer!'
        }
      }
    },
    
    proceedToTiers() {
      if (!this.$refs.entryForm.validate()) {
        return
      }

      // Store user info in localStorage for later use
      localStorage.setItem('sweepstakes_user_info', JSON.stringify(this.userForm))

      // Navigate to entry tiers with user info
      if (this.activeCampaign) {
        this.$router.push({
          name: 'SweepstakesEntry',
          params: { campaignId: this.activeCampaign.id },
          query: {
            userInfo: 'stored', // Flag to indicate user info is stored
            ref: this.referralCode // Pass referral code to entry page
          }
        })
      }
    },
    
    formatPrice(value) {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value)
    },

    onHeroImageError(event) {
      console.warn('Hero image failed to load, using fallback:', event.target.src)
      event.target.src = require('@/assets/img/bg-car.jpg')
    },

    onPrizeImageError(event) {
      console.warn('Prize image failed to load, using fallback:', event.target.src)
      event.target.src = require('@/assets/img/tesla-model-s.png')
    },

    async loadCurrentUser() {
      if (!this.isLoggedIn) {
        this.currentUser = null
        return
      }

      try {
        const response = await window.axios.get('/api/user', {
          headers: {
            Authorization: "Bearer " + localStorage.getItem('token')
          }
        })

        if (response.data && response.data.user) {
          this.currentUser = response.data.user
        }
      } catch (error) {
        console.error('Error loading current user:', error)
        // If token is invalid, clear it
        if (error.response && error.response.status === 401) {
          localStorage.removeItem('token')
          this.currentUser = null
        }
      }
    },

    goToLogin() {
      // Store current URL to return after login
      localStorage.setItem('redirect_after_login', this.$route.fullPath)
      this.$router.push('/login')
    },

    goToLoginFromModal() {
      // Close the modal first
      this.showEntryForm = false
      // Store current URL and referral code for after login
      localStorage.setItem('redirect_after_login', this.$route.fullPath)
      if (this.referralCode) {
        localStorage.setItem('sweepstakes_referral_code', this.referralCode)
      }
      this.$router.push('/login')
    },

    goToReferrals() {
      this.$router.push('/my-referrals')
    },

    goToProfile() {
      this.$router.push('/profile')
    },

    logout() {
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      this.currentUser = null
      this.$toast.success('Logged out successfully', {
        position: 'top-right'
      })
      // Optionally reload the page to refresh the state
      this.$router.go(0)
    }
  }
}
</script>

<style scoped>
/* Header Styles */
.sweepstakes-header {
  z-index: 1000 !important;
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.1) !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  height: 120px !important;
  padding: 1rem 0;
}

.autosync-logo {
  transition: transform 0.3s ease;
  filter: brightness(1.2) contrast(1.1);
}

.autosync-logo:hover {
  transform: scale(1.05);
}

.header-badge {
  animation: glow-pulse 2s ease-in-out infinite alternate;
}

@keyframes glow-pulse {
  from {
    box-shadow: 0 0 5px rgba(255, 107, 53, 0.5);
  }
  to {
    box-shadow: 0 0 20px rgba(255, 107, 53, 0.8), 0 0 30px rgba(255, 107, 53, 0.4);
  }
}

/* Header Actions */
.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.login-btn {
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.1) !important;
  border: 2px solid rgba(255, 107, 53, 0.8) !important;
  color: white !important;
  font-weight: 600;
  transition: all 0.3s ease;
}

.login-btn:hover {
  background: rgba(255, 107, 53, 0.9) !important;
  border-color: rgba(255, 107, 53, 1) !important;
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(255, 107, 53, 0.4);
}

.user-menu-btn {
  backdrop-filter: blur(10px);
  background: rgba(255, 107, 53, 0.9) !important;
  color: white !important;
  font-weight: 600;
  transition: all 0.3s ease;
}

.user-menu-btn:hover {
  background: rgba(255, 107, 53, 1) !important;
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(255, 107, 53, 0.4);
}

.login-from-modal-btn {
  transition: all 0.3s ease;
}

.login-from-modal-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(33, 150, 243, 0.3);
}

.sweepstakes-landing {
  min-height: 100vh;
  background: #f8f9fa;
}

.hero-section {
  position: relative;
  min-height: 100vh;
  overflow: hidden;
}

.hero-background {
  position: relative;
  min-height: 100vh;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
}

.hero-video-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.hero-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.3;
}

.hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(26, 26, 46, 0.8) 0%, rgba(22, 33, 62, 0.7) 50%, rgba(15, 52, 96, 0.8) 100%);
  z-index: 2;
}

.hero-content {
  position: relative;
  z-index: 3;
  min-height: 100vh;
  display: flex;
  align-items: center;
  padding: 10rem 0 2rem 0;
}

.hero-content .v-row {
  width: 100%;
  align-items: center;
}

.hero-content .v-col {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.hero-badge {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
}

.hero-title {
  font-size: 3.5rem;
  font-weight: 900;
  line-height: 1.1;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
  margin-bottom: 1.5rem;
}

.highlight-text {
  color: #ff6b35;
  background: linear-gradient(45deg, #ff6b35, #f7931e);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-subtitle {
  font-size: 1.3rem;
  line-height: 1.6;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
  opacity: 0.9;
}

.prize-value-card {
  background: linear-gradient(135deg, #ff6b35 0%, #f7931e 100%);
  border-radius: 20px;
  padding: 20px;
  display: inline-block;
  box-shadow: 0 10px 30px rgba(255, 107, 53, 0.3);
}

.prize-amount {
  font-size: 2.5rem;
  font-weight: 900;
  color: white;
  line-height: 1;
}

.prize-label {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.cta-button {
  font-size: 1.2rem;
  padding: 18px 40px !important;
  border-radius: 50px;
  text-transform: uppercase;
  font-weight: 900;
  letter-spacing: 1px;
  box-shadow: 0 10px 30px rgba(255, 107, 53, 0.4);
  transition: all 0.3s ease;
}

.cta-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 15px 40px rgba(255, 107, 53, 0.6);
}

.trust-indicators {
  opacity: 0.8;
}

.countdown-wrapper {
  max-width: 500px;
  margin: 0;
}

.car-showcase {
  position: relative;
  display: inline-block;
  max-width: 100%;
  margin-top: 2rem;
}

.prize-car-image {
  max-width: 90%;
  height: auto;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  transform: perspective(1000px) rotateY(-5deg);
  transition: transform 0.3s ease;
}

.prize-car-image:hover {
  transform: perspective(1000px) rotateY(0deg) scale(1.05);
}

.car-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 120%;
  height: 120%;
  background: radial-gradient(circle, rgba(255, 107, 53, 0.2) 0%, transparent 70%);
  transform: translate(-50%, -50%);
  z-index: -1;
  animation: glow 3s ease-in-out infinite alternate;
}

@keyframes glow {
  from { opacity: 0.5; transform: translate(-50%, -50%) scale(1); }
  to { opacity: 0.8; transform: translate(-50%, -50%) scale(1.1); }
}

/* Features Section */
.features-section {
  background: white;
}

.section-title {
  font-size: 2.8rem;
  font-weight: 800;
  color: #1a1a2e;
  margin-bottom: 1rem;
}

.section-subtitle {
  font-size: 1.2rem;
  color: #666;
  max-width: 600px;
  margin: 0 auto;
}

.feature-card {
  padding: 2rem 1rem;
  transition: transform 0.3s ease;
}

.feature-card:hover {
  transform: translateY(-10px);
}

.feature-icon {
  margin-bottom: 1.5rem;
}

.feature-title {
  font-size: 1.4rem;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 1rem;
}

.feature-description {
  color: #666;
  line-height: 1.6;
  font-size: 1rem;
}

/* Stats Section */
.stats-section {
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  color: white;
}

.stat-card {
  padding: 2rem 1rem;
  text-align: center;
}

.stat-number {
  font-size: 3rem;
  font-weight: 900;
  color: #ff6b35;
  line-height: 1;
  margin-bottom: 0.5rem;
}

.stat-label {
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.8);
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 600;
}

/* Footer Styles */
.sweepstakes-footer {
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  color: white;
}

.footer-links {
  font-size: 1rem;
}

.footer-link {
  color: #ff6b35;
  text-decoration: none;
  transition: color 0.3s ease;
}

.footer-link:hover {
  color: #f7931e;
  text-decoration: underline;
}

.footer-text {
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
}

.footer-disclaimer {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.8rem;
  max-width: 600px;
  margin: 0 auto;
}

/* Entry Form Modal */
.entry-form-card {
  overflow: hidden;
}

.entry-form-header {
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  padding: 2rem;
}

.form-header-content {
  display: flex;
  align-items: center;
}

.continue-btn {
  font-size: 1.1rem;
  padding: 16px 40px !important;
  border-radius: 50px;
  text-transform: uppercase;
  font-weight: 700;
  letter-spacing: 1px;
  box-shadow: 0 8px 25px rgba(255, 107, 53, 0.3);
}

/* Mobile Input Enhancements */
.font-size-input input {
  font-size: 16px !important;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}

.input-style .v-input__control .v-input__slot {
  min-height: 48px !important;
}

.input-style .v-text-field__details {
  padding-top: 4px;
}

/* Phone Input Specific Styling */
.input-style input[type="tel"] {
  letter-spacing: 1px;
}

/* Prevent zoom on iOS */
@media screen and (-webkit-min-device-pixel-ratio: 0) {
  .input-style input,
  .input-style textarea,
  .input-style select {
    font-size: 16px !important;
  }
}



/* Desktop Responsive */
@media (min-width: 1200px) {
  .hero-content {
    padding: 12rem 0 4rem 0;
  }

  .car-showcase {
    margin-top: 0;
    margin-left: 2rem;
  }

  .prize-car-image {
    max-width: 85%;
  }
}

@media (min-width: 960px) and (max-width: 1199px) {
  .hero-content {
    padding: 11rem 0 3rem 0;
  }

  .car-showcase {
    margin-top: 1rem;
  }

  .prize-car-image {
    max-width: 90%;
  }

  .hero-title {
    font-size: 3rem;
  }
}

/* Tablet and Mobile Responsive */
@media (max-width: 959px) {
  .hero-content .v-col {
    text-align: center !important;
  }

  .car-showcase {
    margin-top: 3rem;
    order: 2;
  }

  .hero-content .v-col:first-child {
    order: 1;
  }
}

@media (max-width: 768px) {
  .sweepstakes-header {
    height: 100px !important;
    padding: 0.5rem 0;
  }

  .header-badge {
    font-size: 0.7rem !important;
  }

  .hero-content {
    padding: 8rem 0 2rem 0 !important;
  }

  .hero-title {
    font-size: 2.5rem;
    text-align: center;
  }

  .hero-subtitle {
    font-size: 1.1rem;
    text-align: center;
  }

  .section-title {
    font-size: 2.2rem;
  }

  .prize-amount {
    font-size: 2rem;
  }

  .stat-number {
    font-size: 2.5rem;
  }

  .cta-button {
    font-size: 1rem;
    padding: 16px 32px !important;
  }

  .prize-car-image {
    transform: none;
    max-width: 100%;
  }
}

@media (max-width: 480px) {
  .hero-title {
    font-size: 2rem;
    line-height: 1.2;
  }

  .section-title {
    font-size: 1.8rem;
  }

  .feature-title {
    font-size: 1.2rem;
  }

  .stat-number {
    font-size: 2rem;
  }
}
</style>
